/**
 * @author       julian@zumper.com (Julian Napolitan)
 * @copyright    Copyright (c) 2024, Zumper
 */

/** @jsx jsx */
import { css, jsx } from '@emotion/react'
import { BREAKPOINT_MOBILE } from '@zcode-ui/themes'
import { createLazyLoadableSection } from '@zcode/lazy-loadable-section'
import { ErrorFallback, LoadingFallback } from '@zcode/nr/es/shared/components/Fallback'
import type { ErrorBoundaryProps } from 'react-error-boundary'

import { logger } from '../../../logger'

const fallbackStyle = css`
  height: 635px;
  @media (max-width: ${BREAKPOINT_MOBILE}) {
    height: 1000px;
  }
`

const onRenderError: ErrorBoundaryProps['onError'] = (error, info) => {
  logger.error('render error for TelcoWidgetLazyLoadableSection', info, error)
}

const onLoadingError = (error: unknown) => {
  logger.error('loading error for TelcoWidgetLazyLoadableSection', error)
}

const loadFn = /* #__LOADABLE__ */ async () =>
  await import(/* webpackChunkName: "TelcoWidget" */ './TelcoWidget')

const message = `Sorry, we couldn't load internet service providers...`

const handleReload = async () => await TelcoWidgetLazyLoadableSection.reload()

const TelcoWidgetErrorFallback = () => (
  <ErrorFallback css={fallbackStyle} message={message} onClickReload={handleReload} />
)
export const TelcoWidgetLoadingFallback = () => (
  <LoadingFallback css={fallbackStyle} name="similar section" />
)

const options = {
  errorFallback: TelcoWidgetErrorFallback,
  loadingFallback: TelcoWidgetLoadingFallback,
  onRenderError,
  onLoadingError,
}

export const TelcoWidgetLazyLoadableSection = createLazyLoadableSection(loadFn, options)
